/**
 * track.js — LUMINA 转化漏斗埋点（轻量，无第三方依赖）
 *
 * 用法：
 *   import { track } from "../utils/track";
 *   track("liuyao_start", { source: "landing_hero" });
 *
 * 发送策略：
 *   1. console.debug（开发可见）
 *   2. sendBeacon 到 /api/analytics（若后端实现该端点）
 *   3. 预留 window.plausible / window.gtag 钩子
 *
 * 关键漏斗事件：
 *   landing_view        — 着陆页曝光
 *   audit_start         — 点击"生成命盘"
 *   audit_complete      — 命盘生成成功
 *   pricing_view        — 查看定价页
 *   liuyao_start        — 进入六爻页
 *   liuyao_cast_begin   — 开始摇卦仪式
 *   liuyao_cast_done    — 摇卦完成（结果展示）
 *   liuyao_pdf_dl       — 下载六爻 PDF
 *   liuyao_buy_click    — 点击购买六爻额度
 *   register_click      — 点击注册按钮
 *   register_done       — 注册成功
 *   login_done          — 登录成功
 *   upgrade_click       — 点击升级（tier/product）
 *   payment_success     — 支付成功回调
 */

const BEACON_URL = "/api/analytics"; // 后端可选接入点

/**
 * @param {string} event   — 事件名（见上）
 * @param {Object} [props] — 附加属性（source, tier, lang 等）
 */
export function track(event, props = {}) {
  const payload = {
    event,
    ts: Date.now(),
    url: window.location.pathname,
    ...props,
  };

  // 1. 开发调试输出
  if (process.env.NODE_ENV !== "production") {
    console.debug("[LUMINA track]", event, props);
  }

  // 2. Beacon（fire-and-forget，不阻塞页面）
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(BEACON_URL, JSON.stringify(payload));
    }
  } catch (_) {
    // sendBeacon 失败静默忽略
  }

  // 3. Plausible 钩子（若 Vercel 上安装了 Plausible script）
  if (typeof window !== "undefined" && window.plausible) {
    window.plausible(event, { props });
  }

  // 4. Google Analytics 4 钩子（若接入了 gtag）
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", event, props);
  }
}

/**
 * usePageView — 在页面组件 mount 时自动发送 page_view
 * @param {string} page   — 页面标识（audit / liuyao / pricing / ...）
 * @param {Object} [meta] — 附加 meta
 */
export function usePageView(page, meta = {}) {
  // 避免循环依赖，直接用原生 useEffect
  // 调用方需自行 import { useEffect } from "react"
  // 这里导出一个工厂函数，调用方写：
  //   useEffect(() => track("page_view", { page }), []);
  // 或者直接使用下面的 trackPageView 函数
}

/** 同步版：直接调用 */
export const trackPageView = (page, meta = {}) =>
  track("page_view", { page, ...meta });
